import { useState } from "react";
import { toast } from "sonner";
import { Heart, Lock } from "lucide-react";
import { errMsg, http, money } from "@/lib/api";

const PRESETS = [25, 50, 100, 250, 500];
const DISPLAY = [
  { v: "public_amount", label: "Show my name & amount" },
  { v: "name_only", label: "Show my name only" },
  { v: "anonymous", label: "Stay anonymous" },
];

const Pill = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-full border-2 text-sm font-semibold transition-colors ${active ? "bg-mas-ink border-mas-ink text-mas-cream" : "border-mas-sand hover:border-mas-ink"}`}>
    {children}
  </button>
);

export const DonateForm = () => {
  const [recurring, setRecurring] = useState(false);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [f, setF] = useState({ name: "", email: "", display: "public_amount", message: "" });
  const [busy, setBusy] = useState(false);
  const value = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!value || value < 1) return toast.error("Please choose an amount of at least $1");
    if (f.display !== "anonymous" && !f.name.trim()) return toast.error("Add your name, or choose to stay anonymous");
    setBusy(true);
    try {
      const { data } = await http.post("/checkout/donation", { ...f, amount: value, recurring, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white text-ink p-6 sm:p-8 shadow-[10px_10px_0_#ED773C] space-y-6" data-testid="donate-form">
      <div className="grid grid-cols-2 bg-mas-paper p-1 rounded-full">
        {[[false, "One-time"], [true, "Monthly"]].map(([v, l]) => (
          <button key={l} type="button" onClick={() => setRecurring(v)} data-testid={`donation-frequency-${v ? "monthly" : "once"}`}
            className={`py-2.5 rounded-full text-sm font-semibold transition-colors ${recurring === v ? "bg-ink text-shell" : "text-mas-mute"}`}>{l}</button>
        ))}
      </div>
      <div>
        <p className="eyebrow mb-3">Choose an amount</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Pill key={p} active={!custom && amount === p} onClick={() => { setAmount(p); setCustom(""); }} testid={`donation-amount-preset-${p}`}>${p}</Pill>
          ))}
        </div>
        <div className="relative mt-3">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mas-mute">$</span>
          <input type="number" min="1" step="1" placeholder="Other amount" value={custom} onChange={(e) => setCustom(e.target.value)} className="field !pl-7" data-testid="donation-custom-amount-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="field" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="donation-name-input" />
        <input className="field" type="email" required placeholder="Email (for your receipt)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="donation-email-input" />
      </div>
      <div>
        <p className="eyebrow mb-3">On the Donor Wall</p>
        <div className="grid gap-2">
          {DISPLAY.map((d) => (
            <label key={d.v} className={`flex items-center gap-3 px-4 py-3 border rounded-md cursor-pointer transition-colors ${f.display === d.v ? "border-tang bg-mas-paper" : "border-mas-sand"}`} data-testid={`donation-display-${d.v}`}>
              <input type="radio" name="display" checked={f.display === d.v} onChange={() => setF({ ...f, display: d.v })} className="accent-[#ED773C]" />
              <span className="text-sm font-medium">{d.label}</span>
            </label>
          ))}
        </div>
      </div>
      <textarea rows={3} maxLength={500} className="field" placeholder="Leave a message for the community (optional)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="donation-message-input" />
      <button disabled={busy} className="btn-terra w-full !py-4 !text-base" data-testid="donation-submit-button">
        <Heart className="w-4 h-4" /> {busy ? "Opening secure checkout..." : `Give ${money(value || 0)}${recurring ? " / month" : ""}`}
      </button>
      <p className="text-xs text-mas-mute flex items-center gap-1.5 justify-center"><Lock className="w-3 h-3" /> Secure payment by Stripe. Messages appear after a quick review.</p>
    </form>
  );
};
